import { useMemo, useState } from "react";
import type { PracticeOption } from "./types.js";
import styles from "../PipelineBrowser.module.css";

type PracticePickerProps = {
  label: string;
  options: PracticeOption[];
  loading: boolean;
  error: string | null;
  selectedKey: string | null;
  onSelect: (option: PracticeOption) => void;
  disabled?: boolean;
};

// A searchable, scrollable single-select list of practice options.
export function PracticePicker({
  label,
  options,
  loading,
  error,
  selectedKey,
  onSelect,
  disabled,
}: PracticePickerProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, search]);

  return (
    <div className={styles.practicePicker}>
      <div className={styles.filterLabel}>{label}</div>
      <input
        className={styles.filterInput}
        placeholder="Search practices…"
        value={search}
        disabled={disabled || loading}
        onChange={e => setSearch(e.target.value)}
      />
      <div className={styles.practiceList}>
        {loading ? (
          <div className={styles.practiceListEmpty}>Loading…</div>
        ) : error ? (
          <div className={styles.practiceListEmpty}>Error: {error}</div>
        ) : filtered.length === 0 ? (
          <div className={styles.practiceListEmpty}>
            {options.length === 0 ? "Select publication and region first" : "No matches"}
          </div>
        ) : (
          filtered.map(option => (
            <button
              key={option.key}
              type="button"
              disabled={disabled}
              className={
                option.key === selectedKey ? styles.practiceItemActive : styles.practiceItem
              }
              onClick={() => onSelect(option)}
            >
              {option.label}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
